import { useEffect } from "react";
import { useLocation } from "react-router";
import { Card, Page } from "../ui.tsx";

/** Each page's "How this works" opens its guide here, by anchor. */
export const GUIDES = [
  {
    id: "first-result",
    title: "Getting your first result",
    body: [
      "Drop in a vendor's MSA, SLA exhibit or order form on Contracts. ninekeep reads every promise you can enforce and shows each one with the contract's own words.",
      "Settle any open questions. ninekeep preselects the reading that pays you least, so nothing is overstated, then approve. ninekeep finds the vendor's status page and measures the last 12 months.",
    ],
  },
  {
    id: "contracts",
    title: "Reading contracts",
    body: [
      "ninekeep reads each contract for what the vendor promised: availability, support response times, how quickly they must tell you about an incident, the credits owed when they miss, and how to claim them.",
      "Every figure is shown with the contract's own words. When the words could mean two things, ninekeep asks you, and preselects the reading that pays you least.",
      "Nothing counts until an owner or admin approves the terms. Approving also starts monitoring the vendor, with the status page and checks ninekeep proposes; untick anything that's wrong.",
    ],
  },
  {
    id: "measuring",
    title: "How ninekeep measures a month",
    body: [
      "Uptime is (minutes that count − downtime) ÷ minutes that count, where the minutes that count leave out what the contract excludes, such as maintenance announced with enough notice.",
      "Overlapping reports from the status page, ninekeep's checks and your own records is counted once. When a vendor revises an incident, ninekeep counts the reading worth the most under your contract.",
      "Credits come from the contract's credit table and the fee you pay, rounded to the cent at every step, with caps applied. Every month page shows the arithmetic.",
    ],
  },
  {
    id: "evidence",
    title: "Proof, strongest first",
    body: [
      "What the vendor confirmed (its status page, its emails) is strongest, then what ninekeep's checks saw, then what your team reported. Each claim says which it rests on.",
      "Forward or CC vendor support threads to the vendor's forwarding address, on its page or in Settings.",
    ],
  },
  {
    id: "claims",
    title: "Sending claims",
    body: [
      "Each claim letter is drafted from ninekeep's figures and checked before it can be sent: every number must match the calculation, and the tone stays firm and professional.",
      "Send by email with the proof attached, or submit on the vendor's own form and record it as sent another way with the case number.",
      "ninekeep is not a law firm. Letters ask for the credit your agreement provides and never threaten legal action.",
    ],
  },
  {
    id: "credits",
    title: "Recording credits",
    body: [
      "When a vendor replies, ninekeep suggests what the reply means. Confirm it, and record the credit with its invoice reference when it lands. Recovered credits appear on Home and in Claims → Recovered.",
    ],
  },
  {
    id: "claude",
    title: "Asking Claude about your contracts",
    body: [
      "Add ninekeep to Claude (in Cowork, claude.ai or the Claude app) as a custom connector: in Claude, Settings, then Connectors, then Add custom connector, with the address under Settings → General → Claude. Claude sends you to ninekeep to approve it with your own sign-in.",
      "Claude can then answer from what ninekeep knows: your contracts and their terms, each vendor's measured uptime and credits, and your claims and their deadlines. It can also hand ninekeep a contract from your Claude files to read, like an upload.",
      "Claude can't approve terms, send or change claims, or change settings; those stay in ninekeep. Disconnect it any time in Settings.",
    ],
  },
  {
    id: "settings",
    title: "Settings and your team",
    body: [
      "Owners and admins invite people, choose their roles, approve terms and change Settings; members prepare and send claims; viewers can look.",
      "Everyone signs in with two-factor authentication. Deleting the organization, exporting its data, changing roles, single sign-on and claims sent for you ask you to confirm it's you again.",
      "Alerts go to owners and admins by email. Send a test from Settings to check they arrive.",
    ],
  },
];

export function Help() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);
  return (
    <Page title="Help" lead="How ninekeep finds what your vendors owe you, and how to claim it.">
      {GUIDES.map((g) => (
        <Card key={g.id} id={g.id} title={g.title}>
          <div className="flex max-w-3xl flex-col gap-3 text-sm leading-relaxed">
            {g.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </Card>
      ))}
    </Page>
  );
}
